import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, fmtDate, formatApiError, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Trash2, ScanLine, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ListSearch from "@/components/ListSearch";
import { toast } from "sonner";
import axios from "axios";

export default function Receipts() {
    const { currentOrg, features } = useAuth();
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const fileRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/receipts");
        setItems(data.items || []);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        const orgId = localStorage.getItem("bl_org_id");
        try {
            await axios.post(`${API_BASE}/receipts/upload`, fd, {
                withCredentials: true,
                headers: { "X-Org-Id": orgId || "" },
            });
            toast.success("Receipt processed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete?")) return;
        try { await api.delete(`/receipts/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader
                subtitle="Purchases"
                title="Receipts"
                action={
                    <div className="flex items-center gap-2">
                        <Badge className="bg-ocean-50 text-ocean-700 border-0">OCR quota: {features.ocr_per_month ?? "-"} / month</Badge>
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="receipt-file-input" />
                        <Button className="bg-ocean-700 text-white rounded-xl" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-receipt-btn">
                            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload receipt
                        </Button>
                    </div>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={ScanLine} title="No receipts yet" description="Upload a photo of a receipt — we'll extract the merchant, total, and category with GPT-4o vision." />
            ) : (
                <>
                    <div className="mb-4 flex items-center gap-3">
                        <ListSearch value={q} onChange={setQ} placeholder="Search receipts by merchant, category or filename…" testId="receipts-search" />
                        {(() => {
                            const s = q.toLowerCase().trim();
                            const count = s ? items.filter((r) => (
                                (r.parsed?.merchant || "").toLowerCase().includes(s) ||
                                (r.parsed?.suggested_category || "").toLowerCase().includes(s) ||
                                (r.filename || "").toLowerCase().includes(s)
                            )).length : items.length;
                            return s ? <span className="text-xs text-muted-foreground">{count} of {items.length}</span> : null;
                        })()}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.filter((r) => {
                            const s = q.toLowerCase().trim();
                            if (!s) return true;
                            return (
                                (r.parsed?.merchant || "").toLowerCase().includes(s) ||
                                (r.parsed?.suggested_category || "").toLowerCase().includes(s) ||
                                (r.filename || "").toLowerCase().includes(s)
                            );
                        }).map((r) => (
                        <div key={r.id} className="rounded-3xl bg-white border border-sand-200 p-4 shadow-card hover:shadow-card-hover transition-all" data-testid={`receipt-${r.id}`}>
                            <div className="aspect-[4/3] rounded-2xl bg-sand-100 overflow-hidden">
                                {r.data_url && <img src={r.data_url} alt="receipt" className="w-full h-full object-cover" />}
                            </div>
                            <div className="mt-4 flex items-start justify-between">
                                <div>
                                    <div className="font-medium text-ocean-700">{r.parsed?.merchant || r.filename}</div>
                                    <div className="text-xs text-muted-foreground">{r.parsed?.date ? fmtDate(r.parsed.date) : fmtDate(r.created_at)}</div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-sm">
                                <Badge className="bg-secondary/10 text-secondary-dark border-0">{r.parsed?.suggested_category || "Uncategorized"}</Badge>
                                <div className="ml-auto font-mono text-ocean-700">{r.parsed?.total ? fmtMoney(r.parsed.total, r.parsed.currency || "USD") : "—"}</div>
                            </div>
                        </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
